import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { KpiCard, DashboardHeader, SectionTable, formatINR } from "../components/DashboardWidgets";
import { Megaphone, ChartLine, CurrencyInr, ShareNetwork, Trophy, Funnel } from "@phosphor-icons/react";

const KPI = [
  { key: "total_campaigns",     label: "Total Campaigns",   icon: Megaphone,      tone: "blue" },
  { key: "active_campaigns",    label: "Active Campaigns",  icon: ChartLine,      tone: "emerald" },
  { key: "total_social_leads",  label: "Total Leads",       icon: Funnel,         tone: "indigo" },
  { key: "total_spend",         label: "Total Spend",       icon: CurrencyInr,    tone: "amber",  money: true },
  { key: "cost_per_lead",       label: "Cost per Lead",     icon: CurrencyInr,    tone: "amber",  money: true },
  { key: "total_conversions",   label: "Conversions",       icon: Trophy,         tone: "emerald" },
];

const DigitalMarketingDashboard = ({ user, logout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/dashboard/digital-marketing`); setData(r.data); }
    catch { toast.error("Failed to load Digital Marketing dashboard"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8">
          <DashboardHeader overline="Digital Marketing" title="Digital Marketing Dashboard" subtitle="Campaigns, lead source, cost-per-lead & conversion attribution." onRefresh={load} refreshTestId="dm-refresh-btn" />
          {loading ? <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div> : (
            <>
              <div data-testid="dm-kpi-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
                {KPI.map(k => <KpiCard key={k.key} icon={k.icon} label={k.label} tone={k.tone} money={k.money} value={data?.kpis?.[k.key]} testId={`dm-kpi-${k.key}`} />)}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <SectionTable
                  title="By Campaign" icon={Megaphone} testId="dm-by-campaign"
                  columns={[{ label: "Campaign" }, { label: "Leads", align: "right" }, { label: "Conversions", align: "right" }, { label: "Conv %", align: "right" }]}
                  rows={data?.by_campaign || []}
                  emptyText="No campaign attribution yet."
                  renderRow={(r, i) => (
                    <tr key={r.campaign_name + i} data-testid={`dm-campaign-row-${i}`} className="border-b border-slate-100">
                      <td className="p-3 text-sm">{r.campaign_name}</td>
                      <td className="p-3 text-right tabular-nums">{r.lead_count}</td>
                      <td className="p-3 text-right tabular-nums text-emerald-700">{r.conversion_count}</td>
                      <td className="p-3 text-right tabular-nums">{r.conversion_pct}%</td>
                    </tr>
                  )}
                />
                <SectionTable
                  title="By Platform" icon={ShareNetwork} testId="dm-by-platform"
                  columns={[{ label: "Platform" }, { label: "Leads", align: "right" }]}
                  rows={data?.by_platform || []}
                  emptyText="No platform data."
                  renderRow={(r, i) => (
                    <tr key={r.platform + i} data-testid={`dm-platform-row-${i}`} className="border-b border-slate-100">
                      <td className="p-3 text-sm capitalize">{r.platform}</td>
                      <td className="p-3 text-right tabular-nums">{r.lead_count}</td>
                    </tr>
                  )}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DigitalMarketingDashboard;
